import React, { useMemo } from "react";
import _find from "lodash/find";
import _toLower from "lodash/toLower";
import cx from "classnames";
import { FacetCategory } from "shared-types/src/Facets";
import checkIsPerformer from "shared-auth/src/utils/checkIsPerformer";
import { ReactComponent as UserRoleIcon } from "assets/icons/user/user-role.svg";
import { ReactComponent as CoinsIcon } from "assets/icons/commerce/coins.svg";
import { ReactComponent as ClipperboardIcon } from "assets/icons/media/clapperboard.svg";
import { ReactComponent as LocationPinIcon } from "assets/icons/location/location-pin.svg";
import { ReactComponent as CogSettingsIcon } from "assets/icons/actions/cog-settings.svg";
import { IconTextLink } from "component-library/src/components/Atoms/IconTextLink/IconTextLink";
import { Spinner } from "component-library/src/components/Atoms/Spinner/Spinner";
import { useTranslation } from "i18n";
import { JOBS_FEED_SETTINGS_PATHNAME } from "shared/config/common";
import { UrlCommand } from "shared/config/UrlCommand";
import { MY_FILTERS_FULL_PATH } from "config/common";
import { mapContractTypes } from "pages/JobsPage/components/RolePreferences/mappers/map-contract-types";
import { mapProductionTypes } from "pages/JobsPage/components/RolePreferences/mappers/map-production-types";
import { mapProductionCountries } from "pages/JobsPage/components/RolePreferences/mappers/map-production-countries";
import { mapRoleDetails } from "pages/JobsPage/components/RolePreferences/mappers/map-role-details";
import { useRolePreferencesData } from "./useRolePreferencesData";
import PreferenceRow from "./PreferenceRow";

interface RolePreferencesProps {
  className?: string;
}

const RolePreferences = ({ className }: RolePreferencesProps) => {
  const { t } = useTranslation();
  const isPerformer = checkIsPerformer();
  const { facets, settingsData, countries, isLoading, isError } =
    useRolePreferencesData();

  const settings = isPerformer
    ? settingsData?.performerSettings
    : settingsData?.agentSettings;
  const performerSettings = isPerformer
    ? settingsData?.performerSettings
    : null;

  const getContractTypes = useMemo(
    () =>
      mapContractTypes({
        facet: _find(
          facets,
          (facet) =>
            _toLower(facet.systemName) === _toLower(FacetCategory.ContractType)
        ),
        facetIds: settings?.contractTypes || [],
        anyContractType: settings?.anyContractType,
        t,
      }),
    [facets, settings?.anyContractType, settings?.contractTypes, t]
  );

  const getProductionCountries = useMemo(
    () =>
      mapProductionCountries({
        productionCountries: countries || [],
        countryIds: settings?.productionCountries || [],
        anyProductionCountry: settings?.anyProductionCountry,
        t,
      }),
    [
      countries,
      settings?.anyProductionCountry,
      settings?.productionCountries,
      t,
    ]
  );

  const getProductionTypes = useMemo(
    () =>
      mapProductionTypes({
        facets,
        productionTypeIds: settings?.productionTypes || [],
        anyProductionType: settings?.anyProductionType,
        t,
      }),
    [facets, settings?.anyProductionType, settings?.productionTypes, t]
  );

  const getRoleDetails = useMemo(
    () =>
      mapRoleDetails({
        facets,
        t,
        anyGender: performerSettings?.anyGender,
        genderIds: performerSettings?.genderIds || [],
        anyAppearance: performerSettings?.anyAppearance,
        appearanceIds: performerSettings?.appearanceIds || [],
        minPlayingAge: settings?.minPlayingAge,
        maxPlayingAge: settings?.maxPlayingAge,
        minimumHeight: performerSettings?.minimumHeight,
        maximumHeight: performerSettings?.maximumHeight,
      }),
    [
      facets,
      settings?.maxPlayingAge,
      settings?.minPlayingAge,
      performerSettings?.anyAppearance,
      performerSettings?.anyGender,
      performerSettings?.appearanceIds,
      performerSettings?.genderIds,
      performerSettings?.maximumHeight,
      performerSettings?.minimumHeight,
      t,
    ]
  );

  return (
    <section className={cx("rounded-2xl bg-indigo-50/60 p-2.5", className)}>
      <header className="mb-4 flex flex-col items-start justify-between gap-1.5 sm:flex-row">
        <h2 className="text-m font-semibold leading-none">
          {t("common:jobs.page.rolePreferences.title")}
        </h2>
        <IconTextLink
          variant="button-icon-text-secondary"
          svgIconStart={<CogSettingsIcon />}
          text={t("common:jobs.page.rolePreferences.editPreferences")}
          className="w-full justify-center sm:w-auto"
          linkUrl={`${JOBS_FEED_SETTINGS_PATHNAME}?${UrlCommand.ReturnUri}=${MY_FILTERS_FULL_PATH}`}
        />
      </header>
      {isLoading && <Spinner inline />}
      {isError && (
        <p className="text-orange-dark">
          {t("common:errorPopup.httpStatus.generic.body")}
        </p>
      )}
      {Boolean(!isLoading && !isError) && (
        <div className="flex flex-col gap-3">
          <PreferenceRow
            label={t("common:jobs.page.rolePreferences.roleDetails")}
            labelClassName="bg-pink-pale-rose text-pink-rose"
            icon={<UserRoleIcon />}
            value={getRoleDetails}
          />
          <PreferenceRow
            label={t("common:jobs.page.rolePreferences.contractTypesAndSender")}
            labelClassName="bg-orange-pale-2 text-orange-dark"
            icon={<CoinsIcon />}
            value={getContractTypes}
          />
          <PreferenceRow
            label={t("common:jobs.page.rolePreferences.productionTypes")}
            labelClassName="bg-purple-pale text-purple-rich"
            icon={<ClipperboardIcon />}
            value={getProductionTypes}
          />
          <PreferenceRow
            label={t("common:jobs.page.rolePreferences.productionCountries")}
            labelClassName="bg-teal-pale text-teal-deep"
            icon={<LocationPinIcon />}
            value={getProductionCountries}
          />
        </div>
      )}
    </section>
  );
};

export default RolePreferences;
